<mat-toolbar class="primary">
  <span class="adx-address-book-icon"><i class="fa-solid fa-address-book"></i></span>
  @if (data.source === ContactSource.MANUAL){
    {{'ASTIMAX.CONTACT.WIDGET.MANUAL_ADDRESS_BOOK' | translate }}
  }
  @else if (data.source === ContactSource.EXTENSION) {
  {{'ASTIMAX.CONTACT.WIDGET.EXTENSION_ADDRESS_BOOK' | translate }}
}
<span class="adx-toolbar-spacer"></span>
<button mat-icon-button
  [mat-dialog-close]="true"
  aria-label="Close Button"
  matTooltip="{{'ASTIMAX.CONTACT.WIDGET.CLOSE' | translate }}">
  <fa-icon [icon]="'xmark'"></fa-icon>
</button>
</mat-toolbar>

<mat-dialog-content class="adx-dialog-content-wrapper">
  <!-- Loading State -->
  @if (isLoading()) {
    <div class="adx-loading-state">
      <mat-spinner diameter="40"></mat-spinner>
      <p>{{'ASTIMAX.CONTACT.WIDGET.TRYING_TO_LOAD_EXTENSION_CONTACTS' | translate }}...</p>
    </div>
  }

  <!-- Address Book Description -->
  @if(!isLoading()) {
    <div class="adx-dialog-header">
      <h3>{{ getSourceDescription() }}.</h3>
    </div>
  }

  <!-- Search Bar -->
  @if (!isLoading()) {
    <div class="adx-search-container">
      <mat-form-field appearance="outline" class="adx-search-field">
        <mat-label>{{'ASTIMAX.CONTACT.WIDGET.SEARCH_CONTACTS' | translate }}</mat-label>
        <i class="fas fa-search" matPrefix aria-hidden="true"></i>
        <input
          matInput
          placeholder="{{'ASTIMAX.CONTACT.WIDGET.SEARCH_CONTACTS' | translate }}..."
          [(ngModel)]="searchTerm"
          (input)="onSearchChange($event)"
          [ngModelOptions]="{standalone: true}"
          aria-label="Search contacts"
          aria-describedby="search-help"
          role="searchbox">
        </mat-form-field>
        <!-- Contact Management for Manual Contacts -->
        @if (data.source === ContactSource.MANUAL && !isLoading()) {
          <div class="adx-manual-contacts-actions">
            <button mat-raised-button
              class="primary adx-manual-contact-create-btn"
              (click)="createManualContact()"
              [attr.aria-label]="'Create new manual contact'"
              matTooltip="{{'ASTIMAX.CONTACT.WIDGET.CREATE_MANUAL_CONTACT' | translate }}">
              <i class="fas fa-plus" aria-hidden="true"></i>
            </button>
          </div>
        }
      </div>
    }

    <!-- Selection Actions with Sorting Controls -->
    @if (!isLoading()) {
      <div class="adx-select-actions">
        <div class="adx-button-group">
          <!-- Sorting Button -->
          <button
            mat-raised-button
            [class.active]="sortBy() === 'alphabetical'"
            (click)="setSortBy(sortBy() === 'alphabetical' ? 'default' : 'alphabetical')"
            type="button"
            class="adx-action-btn adx-sort-btn"
            matTooltip="{{'ASTIMAX.CONTACT.WIDGET.SORT_ALPHABETICALLY' | translate }}">
            <i class="fas fa-sort-alpha-down" aria-hidden="true"></i>
            {{'ASTIMAX.CONTACT.WIDGET.ALPHABETICAL' | translate }}
          </button>
        </div>
        <div class="adx-button-group adx-action-buttons">
          <!-- Action Buttons -->
          <button
            mat-raised-button
            (click)="selectAllContacts()"
            [disabled]="filteredAndSortedContacts().length === 0"
            type="button"
            class="adx-action-btn select-btn"
            [attr.aria-label]="'Select all ' + filteredAndSortedContacts().length + ' displayed contacts'"
            matTooltip="{{'ASTIMAX.CONTACT.WIDGET.SELECT_ALL' | translate }}">
            <i class="fas fa-check-double" aria-hidden="true"></i>
            {{'ASTIMAX.CONTACT.WIDGET.SELECT_ALL' | translate }}
          </button>

          <button
            mat-raised-button
            (click)="clearAllSelections()"
            [disabled]="selectedContactIds().size === 0 && alreadySelectedCount === 0"
            type="button"
            class="adx-action-btn clear-btn"
            [attr.aria-label]="'Clear selection of ' + selectedContactIds().size + ' contacts'"
            matTooltip="{{'ASTIMAX.CONTACT.WIDGET.CANCEL_SELECTION' | translate }}">
            <i class="fas fa-times" aria-hidden="true"></i>
            {{'ASTIMAX.CONTACT.WIDGET.CANCEL_SELECTION' | translate }}
          </button>
        </div>
      </div>
    }

    <!-- Contact List -->
    <div class="adx-contact-list-container">
      @if (!isLoading() && filteredAndSortedContacts().length === 0 && searchTerm.trim()) {
        <div class="adx-no-results">
          <i class="fas fa-search" aria-hidden="true"></i>
          <span>{{'ASTIMAX.CONTACT.WIDGET.NO_CONTACTS_FOUND_FOR' | translate }} "{{ searchTerm }}"</span>
        </div>
      }

      @if (!isLoading() && availableContacts().length === 0) {
        <div class="adx-no-contacts">
          <h3>{{'ASTIMAX.CONTACT.WIDGET.NO' | translate }} "{{ getSourceTitle() }}"  {{'ASTIMAX.CONTACT.WIDGET.AVAILABLE' | translate }}</h3>
          <p>{{ getEmptyStateMessage() }}</p>
        </div>
      }

      @if (!isLoading() && filteredAndSortedContacts().length > 0) {
        <div class="adx-contact-list">
          @for (contact of filteredAndSortedContacts(); track contact.contactId + contact.source) {
            <div class="adx-contact-item"
              [class.selected]="isContactSelected(contact) && !isAlreadySelected(contact)"
              [class.already-selected]="isAlreadySelected(contact) && isContactSelected(contact)"
              [class.already-selected-removing]="isAlreadySelected(contact) && !isContactSelected(contact)"
              [class.manual-contact]="ContactUtils.isManualContact(contact)"
              [class.extension-contact]="ContactUtils.isExtensionContact(contact)"
              (click)="toggleContact(contact)"
              (keydown.enter)="toggleContact(contact)"
              (keydown.space)="$event.preventDefault(); toggleContact(contact)"
              [attr.aria-selected]="isContactSelected(contact)"
              [attr.aria-label]="getContactAriaLabel(contact)"
              tabindex="0">

              <div class="adx-contact-avatar">
                @if (contact.avatar) {
                  <img
                    [src]="contact.avatar"
                    [alt]="'Profile picture of ' + contact.name"
                    class="adx-avatar-image">
                }
                @if (!contact.avatar) {
                  <div
                    class="adx-avatar-initials">
                    {{ contact.initials || ContactUtils.getInitials(contact.name) }}
                  </div>
                }
              </div>

              <div class="adx-contact-info">
                <div class="adx-contact-name">{{ contact.name }}</div>
                <div class="adx-contact-details">
                  <span class="adx-phone-number">[{{ ContactUtils.getDisplayPhoneNumber(contact) }}]</span>
                </div>
              </div>

              <!-- Manual Contact Actions -->
              @if (ContactUtils.isManualContact(contact)) {
                <div class="adx-contact-actions" (click)="$event.stopPropagation()">
                  <button mat-icon-button
                    (click)="editManualContact(contact)"
                    [attr.aria-label]="'Edit ' + contact.name"
                    matTooltip="{{'ASTIMAX.CONTACT.WIDGET.EDIT_CONTACT' | translate }}"
                    class="adx-edit-action">
                    <i class="fas fa-pencil"></i>
                  </button>
                  <button mat-icon-button
                    (click)="deleteManualContact(contact)"
                    [attr.aria-label]="'Delete ' + contact.name"
                    matTooltip="{{'ASTIMAX.CONTACT.WIDGET.DELETE_CONTACT' | translate }}"
                    class="adx-delete-action">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              }

              <div class="adx-contact-selection-indicator">
                @if (isAlreadySelected(contact) && isContactSelected(contact)) {
                  <i class="fas fa-check-circle adx-already-selected-icon contact-icon"
                  [attr.title]="getSelectionIndicatorDescription('alreadySelected')"></i>
                } @else if (isContactSelected(contact) && !isAlreadySelected(contact)) {
                  <i class="fas fa-check-circle adx-selected-icon contact-icon"
                  [attr.title]="getSelectionIndicatorDescription('selected')"></i>
                } @else if (isAlreadySelected(contact) && !isContactSelected(contact)) {
                  <i class="fas fa-minus-circle adx-deselected-icon contact-icon"
                  [attr.title]="getSelectionIndicatorDescription('deselected')"></i>
                } @else {
                  <i class="far fa-circle adx-unselected-icon contact-icon"
                  [attr.title]="getSelectionIndicatorDescription()"></i>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>

    <!-- Selection Summary -->
    @if (!isLoading() && availableContacts().length > 0) {
      <div class="adx-selection-summary">
        <i class="fas fa-info-circle" aria-hidden="true"></i>
        {{'ASTIMAX.CONTACT.WIDGET.TOTAL_NUMBER' | translate }}: {{ availableContacts().length }}
        <span class="adx-selection-summary-divider"></span>
        @if (alreadySelectedCount > 0) {
          <mat-chip aria-label="selection summary" class="adx-already-selected-info adx-non-clickable">{{'ASTIMAX.CONTACT.WIDGET.IN_TOTAL' | translate }} {{ alreadySelectedCount }} {{'ASTIMAX.CONTACT.WIDGET.CONTACTS' | translate }} {{'ASTIMAX.CONTACT.WIDGET.IN_GROUP' | translate }}</mat-chip>
        }
      </div>
    }
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button mat-raised-button
      (click)="cancel()"
      type="button">
      {{'ASTIMAX.CONTACT.WIDGET.CANCEL' | translate }}
    </button>
    <button mat-raised-button
      class="primary"
      (click)="confirm()"
      [disabled]="!hasChangesToApply()"
      type="button">
      {{'ASTIMAX.CONTACT.WIDGET.APPLY_CHANGES' | translate }}
    </button>
  </mat-dialog-actions>
